import { useStrictLocalization } from "@faire/web--source/common/localization";

import { QuickAction, QuickActionType } from "../types";

import { useBrandQuickActions } from "./useBrandQuickActions";
import { useRetailerQuickActions } from "./useRetailerQuickActions";

export interface IModalContent {
  title?: QuickAction["label"];
  description?: string;
  buttonText?: string;
}

type ModalContentType = Record<keyof typeof QuickActionType, IModalContent>;

export const useModalContent = (): ModalContentType => {
  const { strictLocalize } = useStrictLocalization();
  const retailerQuickActions = useRetailerQuickActions();
  const brandQuickActions = useBrandQuickActions();
  const quickActions = { ...retailerQuickActions, ...brandQuickActions };

  return {
    [QuickActionType.ACCEPT_AN_ORDER]: {
      title: quickActions[QuickActionType.ACCEPT_AN_ORDER]?.label,
      description: strictLocalize({
        defaultMessage: "Accept new orders by providing a shipping estimate",
        description: {
          text: "Body for the quick action of accepting an order",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Provide Shipping Estimate",
        description: {
          text: "Action of accepting an order",
        },
      }),
    },
    [QuickActionType.REQUEST_COMMISSION_CHANGE]: {
      title: quickActions[QuickActionType.REQUEST_COMMISSION_CHANGE]?.label,
      description: strictLocalize({
        defaultMessage:
          "Request a Faire Direct commission update on orders for retailers you have an existing wholesale relationship with.",
        description: {
          text: "Body for the quick action of requesting commission update",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Start Commission Update Request",
        description: {
          text: "Action of requesting commission update",
        },
      }),
    },
    [QuickActionType.EDIT_ITEM_AVAILABILITY]: {
      title: quickActions[QuickActionType.EDIT_ITEM_AVAILABILITY]?.label,
      description: strictLocalize({
        defaultMessage: "Update availability for items in unfulfilled orders",
        description: {
          text: "Body for the quick action of editting item availability",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Edit Item Availability",
        description: {
          text: "Action of editting item availability",
        },
      }),
    },
    [QuickActionType.CANCEL_AN_ORDER]: {
      title: quickActions[QuickActionType.CANCEL_AN_ORDER]?.label,
      description: strictLocalize({
        defaultMessage:
          "Cancel an order that hasn't been fulfilled or shipped yet",
        description: {
          text: "Body for the quick action of canceling an order",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Start Cancellation",
        description: {
          text: "Action of canceling an order",
        },
      }),
    },
    [QuickActionType.TRACK_AN_ORDER]: {
      title: quickActions[QuickActionType.TRACK_AN_ORDER]?.label,
      description: strictLocalize({
        defaultMessage:
          "View tracking details for orders that have been shipped",
        description: {
          text: "Body for the quick action of tracking an order",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Track Order",
        description: {
          text: "Action of tracking an order",
        },
      }),
    },
    [QuickActionType.RETURN_ITEMS]: {
      title: quickActions[QuickActionType.RETURN_ITEMS]?.label,
      description: strictLocalize({
        defaultMessage:
          "Return items from orders received within the last 60 days. Only your first order from a specific brand is returnable.",
        description: {
          text: "Body for the quick action of starting a return",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Start a Return",
        description: {
          text: "Action of starting a return",
        },
      }),
    },
    [QuickActionType.CONTACT_A_BRAND]: {
      title: quickActions[QuickActionType.CONTACT_A_BRAND]?.label,
      description: strictLocalize({
        defaultMessage:
          "Contact brands for specific questions about the product or concerns about the timing of an order. This is the fastest and best way to resolve outstanding questions.",
        description: {
          text: "Body for the quick action of contacting a brand",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Contact Brand",
        description: {
          text: "Action of contacting a brand",
        },
      }),
    },
    [QuickActionType.REPORT_DAMAGED_OR_MISSING_ITEMS]: {
      title:
        quickActions[QuickActionType.REPORT_DAMAGED_OR_MISSING_ITEMS]?.label,
      description: strictLocalize({
        defaultMessage:
          "Report issues about your order within 14 days of delivery",
        description: {
          text: "Body for the quick action of reporting some items",
        },
      }),
      buttonText: strictLocalize({
        defaultMessage: "Start Damaged/Missing Report",
        description: {
          text: "Action of reporting some items",
        },
      }),
    },
  };
};
